// CSV shapes for the Bookings and Traffic payloads. One section per panel on
// the page, in the order the page renders them, so the file reads like the
// dashboard rather than like the JSON.

function kpiRows(kpis) {
  return Object.entries(kpis).map(([key, m]) => [key, m?.value ?? '', m?.previous ?? ''])
}

function filterMeta(stats, pairs) {
  return [
    ['Generated', new Date().toISOString()],
    ['Range', `${stats.range.start} to ${stats.range.end}`],
    ['Timezone', stats.range.zone || ''],
    ...pairs,
  ]
}

export function bookingsCsv(stats, filters, options = {}) {
  // Event ids are meaningless in a spreadsheet, so they are resolved back to
  // names through the same option list the dropdown renders.
  const eventName = (id) => options.events?.find(e => e.value === id)?.label ?? id

  return {
    meta: filterMeta(stats, [
      ['Events', filters.eventIds.map(eventName).join(' | ') || 'all'],
      ['Statuses', filters.statuses.join(' ') || 'all'],
      ['Locations', filters.locations.join(' ') || 'all'],
      ['Booked by', filters.bookedBy || 'anyone'],
    ]),
    sections: [
      { name: 'Headline figures', headers: ['Metric', 'Value', 'Previous period'], rows: kpiRows(stats.kpis) },
      {
        name: 'Over time',
        headers: ['Date', 'Scheduled', 'Completed', 'Canceled'],
        rows: stats.over_time.map(d => [d.date, d.created, d.completed, d.canceled]),
      },
      {
        name: 'By weekday',
        headers: ['Weekday', 'Bookings'],
        rows: stats.by_weekday.map(d => [d.name, d.count]),
      },
      {
        name: 'By hour',
        headers: ['Hour', 'Bookings'],
        rows: stats.by_hour.map(d => [d.hour, d.count]),
      },
      {
        name: 'Lead time',
        headers: ['Bucket', 'Bookings'],
        rows: stats.lead_time.map(d => [d.bucket, d.count]),
      },
      {
        name: 'Top events',
        headers: ['Event', 'Bookings', 'Share %'],
        rows: stats.top_events.map(d => [d.name, d.count, d.share]),
      },
      {
        name: 'Top attendees',
        headers: ['Attendee', 'Email', 'Bookings', 'Last booked'],
        rows: stats.top_attendees.map(d => [d.name, d.email, d.count, d.last_booked_at || '']),
      },
      {
        name: 'Meeting locations',
        headers: ['Location', 'Bookings', 'Share %'],
        rows: stats.locations.map(d => [d.name, d.count, d.share]),
      },
    ],
  }
}

export function trafficCsv(stats, filters) {
  return {
    meta: filterMeta(stats, [
      ['Channels', filters.channels.join(' | ') || 'all'],
      ['Countries', filters.countries.join(' | ') || 'all'],
      ['Devices', filters.devices.join(' ') || 'all'],
      ['Page type', filters.pageType || 'all'],
    ]),
    sections: [
      { name: 'Headline figures', headers: ['Metric', 'Value', 'Previous period'], rows: kpiRows(stats.kpis) },
      {
        name: 'Visitors over time',
        headers: ['Date', 'Total', 'New', 'Returning'],
        rows: stats.over_time.map(d => [d.date, d.total, d.new, d.returning]),
      },
      {
        name: 'Funnel',
        headers: ['Step', 'People', 'Share of top %', 'Step rate %', 'Dropped'],
        rows: stats.funnel.map(d => [d.label, d.count, d.share_of_top ?? '', d.step_rate ?? '', d.dropped ?? '']),
      },
      {
        name: 'By country',
        headers: ['Country', 'Visitors', 'Conversions', 'Conversion rate %'],
        rows: stats.by_country.map(d => [d.name, d.visitors, d.conversions, d.conversion_rate ?? '']),
      },
      {
        name: 'Devices',
        headers: ['Platform', 'Visitors', 'Share %'],
        rows: stats.devices.platform.map(d => [d.name, d.visitors, d.share]),
      },
      {
        name: 'Device conversion',
        headers: ['Device', 'Visitors', 'Conversions', 'Conversion rate %'],
        rows: stats.device_conversion.map(d => [d.name, d.visitors, d.conversions, d.conversion_rate ?? '']),
      },
      {
        name: 'Sources',
        headers: ['Channel', 'Visitors', 'Conversions', 'Conversion rate %'],
        rows: stats.sources.map(d => [d.channel, d.visitors, d.conversions, d.conversion_rate ?? '']),
      },
      {
        name: 'Landing pages',
        headers: ['Path', 'Visitors'],
        rows: stats.landing_pages.map(d => [d.path, d.visitors]),
      },
      {
        name: 'Campaigns',
        headers: ['Source', 'Medium', 'Campaign', 'Visitors', 'Conversions'],
        rows: stats.campaigns.map(d => [d.source, d.medium, d.campaign || '', d.visitors, d.conversions]),
      },
      {
        name: 'Peak hours',
        headers: ['Hour', 'Visitors'],
        rows: stats.peak_hours.map(d => [d.hour, d.visitors]),
      },
      {
        name: 'Where bookings are lost',
        headers: ['Event', 'Opened', 'No slots', 'No slots rate %', 'Form failures'],
        rows: stats.dead_ends.map(d => [d.name, d.opened, d.no_slots, d.no_slots_rate ?? '', d.failed]),
      },
    ],
  }
}
